import { API } from 'aws-amplify';
import ApiEnum, { ApiInfo } from '@/enums/ApiEnum';
import ApiException, { ApiErrorCode } from '@/exceptions/ApiException';
import * as ApiIF from '@/models/ApiIF';
import config from '@/config/config';
import StoreHelper from './StoreHelper';

export default class ApiHelper {
  /**
   * 送信用リクエストボディを「リアクティブでないプレーンオブジェクト」へ複製する。
   *
   * encodeProperty はボディを破壊的に書き換えるため、Vue3 のリアクティブ Proxy
   * (prop / store 由来のオブジェクト等)をそのまま渡すと元データまで Base64 で汚染されてしまう。
   * 送信前に複製することで呼び出し側のデータを不変に保つ。
   *
   * 値・キー構成は変更しない。JSON 直列化により値が undefined のキーは除外されるが、
   * これは元々 API 送信時(JSON 化)に送られないため、送信されるリクエストボディは従来と同一。
   */
  private static toPlainObject<T>(obj: T): T {
    return JSON.parse(JSON.stringify(obj));
  }

  private static encodeProperty(obj: Record<string, unknown>, keys: string[]) {
    Object.keys(obj).map(k => {
      if (obj[k] && keys.includes(k)) {
        // Base64でエンコード
        obj[k] = Buffer.from(String(obj[k])).toString('base64'); // オブジェクトは参照渡しなのでbody内の値も更新される
      }
      //オブジェクトの中身がまたオブジェクトの場合
      if (obj[k] && typeof obj[k] === 'object') {
        ApiHelper.encodeProperty(obj[k] as Record<string, unknown>, keys);
      }
    });
  }

  private static decodeProperty(obj: Record<string, unknown>, keys: string[]) {
    Object.keys(obj).map(k => {
      if (obj[k] && keys.includes(k)) {
        // Base64でエンコードされている文字列をデコード
        obj[k] = Buffer.from(String(obj[k]), 'base64').toString();
      }
      //オブジェクトの中身がまたオブジェクトの場合
      if (obj[k] && typeof obj[k] === 'object') {
        ApiHelper.decodeProperty(obj[k] as Record<string, unknown>, keys);
      }
    });
  }

  public static async post(apiInfo: ApiInfo, body: object | null = null) {
    try {
      StoreHelper.updateLastUseTime();
      const headers = {
        'X-Session-Id': StoreHelper.sessionId() ?? undefined,
        'App-Service-Id': this.getAppServiceId(apiInfo.path),
      };
      //エンコードとデコード対処プロパティ
      const targetProperties = config.encodeTargetProperties;
      // 呼び出し側(リアクティブな可能性のある)データを破壊しないよう、送信用に複製してからエンコードする
      const requestBody = body ? ApiHelper.toPlainObject(body) : body;
      if (requestBody) {
        ApiHelper.encodeProperty(requestBody as Record<string, unknown>, targetProperties);
      }
      const res = await API.post(apiInfo.endpointType, apiInfo.path, {
        response: true,
        body: requestBody,
        headers,
      });
      if (res.status === 200) {
        StoreHelper.maintenance = res.headers['x-maintenance'] ? true : false;
        ApiHelper.decodeProperty(res, targetProperties);
        return res;
      } else {
        throw this.createApiException(res);
      }
    } catch (err) {
      throw this.createApiException(err.response, err);
    }
  }

  /**
   * アプリサービスIDを取得する。
   * @param {string} apiPath
   * @returns {number} 1:mottosokka、4:朝小
   */
  private static getAppServiceId(apiPath: string) {
    let appServiceId = 1;
    if (apiPath === '/get-asagaku-list') {
      appServiceId = 4;
    }
    return appServiceId;
  }

  private static createApiException(
    res: { data?: { error?: { code?: ApiErrorCode; details?: Record<string, unknown> } } },
    err?: unknown
  ) {
    if (res?.data?.error?.code) {
      return new ApiException(res.data.error.code, res.data.error.details);
    } else {
      logger.error(err ?? res);
      return new ApiException('SERVER.INTERNAL_ERROR');
    }
  }

  public static async isAllowIp(req: ApiIF.IsAllowIpRequest) {
    const res = await this.post(ApiEnum.auth.isAllowIp, req);
    return res.data as ApiIF.IsAllowIpResponse;
  }

  public static async login(req: ApiIF.LoginRequest) {
    const res = await this.post(ApiEnum.auth.login, req);
    return res.data as ApiIF.LoginResponse;
  }

  public static async ipLogin(req: ApiIF.IpLoginRequest) {
    const res = await this.post(ApiEnum.auth.ipLogin, req);
    return res.data as ApiIF.IpLoginResponse;
  }

  public static async logout(req: ApiIF.LogoutRequest) {
    const res = await this.post(ApiEnum.auth.logout, req);
    return res.data as ApiIF.LogoutResponse;
  }

  public static async getUser(req: ApiIF.GetUserRequest) {
    const res = await this.post(ApiEnum.user.getUser, req);
    return res.data as ApiIF.GetUserResponse;
  }

  public static async updateUser(req: ApiIF.UpdateUserRequest) {
    const res = await this.post(ApiEnum.user.updateUser, req);
    return res.data as ApiIF.UpdateUserResponse;
  }

  public static async updatePassword(req: ApiIF.UpdatePasswordRequest) {
    const res = await this.post(ApiEnum.user.updatePassword, req);
    return res.data as ApiIF.UpdatePasswordResponse;
  }

  public static async getNotification(req: ApiIF.GetNotificationRequest) {
    const res = await this.post(ApiEnum.ops.getNotification, req);
    return res.data as ApiIF.GetNotificationResponse;
  }

  public static async getSchoolList(req: ApiIF.GetSchoolListRequest) {
    const res = await this.post(ApiEnum.master.getSchoolList, req);
    return res.data as ApiIF.GetSchoolListResponse;
  }

  public static async getSetNameList(req: ApiIF.GetSetNameListRequest) {
    const res = await this.post(ApiEnum.master.getSetNameList, req);
    return res.data as ApiIF.GetSetNameListResponse;
  }

  public static async clientRegistration(req: ApiIF.ClientRegistrationRequest) {
    const res = await this.post(ApiEnum.clientRegistration.clientRegistration, req);
    return res.data as ApiIF.ClientRegistrationResponse;
  }

  public static async sharedidClientRegistration(req: ApiIF.SharedidClientRegistrationRequest) {
    const res = await this.post(ApiEnum.clientRegistration.sharedidClientRegistration, req);
    return res.data as ApiIF.SharedidClientRegistrationResponse;
  }

  public static async validateSchoolContractSelection(req: ApiIF.ValidateSchoolContractSelectionRequest) {
    const res = await this.post(ApiEnum.clientRegistration.validateSchoolContractSelection, req);
    return res.data as ApiIF.ValidateSchoolContractSelectionResponse;
  }

  public static async nextgigaClientRegistration(req: ApiIF.NextgigaClientRegistrationRequest) {
    const res = await this.post(ApiEnum.clientRegistration.nextgigaClientRegistration, req);
    return res.data as ApiIF.NextgigaClientRegistrationResponse;
  }

  public static async fixedipClientRegistration(req: ApiIF.FixedipClientRegistrationRequest) {
    const res = await this.post(ApiEnum.clientRegistration.fixedipClientRegistration, req);
    return res.data as ApiIF.FixedipClientRegistrationResponse;
  }

  public static async getAsagakuList(req: ApiIF.GetAsagakuListRequest) {
    const res = await this.post(ApiEnum.news.getAsagakuList, req);
    return res.data as ApiIF.GetAsagakuListResponse;
  }

  public static async getServiceStatus(req: ApiIF.GetServiceStatusRequest) {
    const res = await this.post(ApiEnum.maintenance.getServiceStatus, req);
    return res.data as ApiIF.GetServiceStatusResponse;
  }

  public static async getPeriodControl(req: ApiIF.GetPeriodControlRequest) {
    const res = await this.post(ApiEnum.master.getPeriodControl, req);
    return res.data as ApiIF.GetPeriodControlResponse;
  }

  public static async getWhitelist(req: ApiIF.GetWhitelistRequest) {
    const res = await this.post(ApiEnum.master.getWhitelist, req);
    return res.data as ApiIF.GetWhitelistResponse;
  }

  public static async operationEvent(req: ApiIF.OperationEventRequest) {
    await this.post(ApiEnum.event.operationEvent, req);
  }

  public static async microsoftSsoLogin(req: ApiIF.MicrosoftSsoLoginRequest) {
    const res = await this.post(ApiEnum.auth.MicrosoftSsoLogin, req);
    return res.data as ApiIF.MicrosoftSsoLoginResponse;
  }

  public static async googleSsoLogin(req: ApiIF.GoogleSsoLoginRequest) {
    const res = await this.post(ApiEnum.auth.GoogleSsoLogin, req);
    return res.data as ApiIF.GoogleSsoLoginResponse;
  }
}
